import { useEffect, useState } from "react";
import { Save, Info, PlugZap, Loader2, KeyRound, Upload } from "lucide-react";
import { toast } from "sonner";
import { botPatch, botGet, auth as authApi, apiError } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Settings() {
  const { guild, refresh } = useData();
  const { demoMode } = useAuth();
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [health, setHealth] = useState(null);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confPw, setConfPw] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  const changePassword = async () => {
    if (newPw.length < 6) return toast.error("La nuova password deve avere almeno 6 caratteri");
    if (newPw !== confPw) return toast.error("Le due password non coincidono");
    setChangingPw(true);
    try {
      await authApi.changePassword(curPw, newPw);
      toast.success("Password aggiornata");
      setCurPw(""); setNewPw(""); setConfPw("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setChangingPw(false); }
  };

  useEffect(() => {
    if (guild) { setName(guild.name || ""); setIcon(guild.icon || ""); setDesc(guild.description || ""); }
  }, [guild]);

  const save = async () => {
    setSaving(true);
    try {
      await botPatch("guild", { name, icon: icon || null, description: desc });
      await refresh();
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    setHealth(null);
    try {
      const h = await botGet("health");
      setHealth(h);
      if (h?.ok || h?.ready) toast.success("Bot raggiungibile");
      else toast.error("Il bot ha risposto ma non è pronto");
    } catch (e) { setHealth({ error: apiError(e) }); toast.error(apiError(e)); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">Impostazioni server</h1>
        <p className="text-sm text-[#949BA4]">Nome, icona e descrizione del server.</p>
      </div>

      <Card className="max-w-2xl border-[#232838] bg-[#161923] p-6">
        <div className="mb-4 flex items-center gap-4">
          {icon ? (
            <img src={icon} alt="" className="h-16 w-16 rounded-2xl object-cover" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#131620] text-xs text-[#949BA4]">Icona</div>
          )}
          <div className="flex-1">
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">URL / data icona</label>
            <div className="flex gap-1.5">
              <Input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="Carica un'immagine dal PC o incolla un link"
                data-testid="settings-icon-input" className="border-[#131620] bg-[#131620] text-white" />
              <label className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-[#232838] bg-[#131620] px-2.5 text-xs font-semibold text-[#c7cbfb] hover:text-white">
                <Upload className="h-4 w-4" />Carica
                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 8 * 1024 * 1024) { toast.error("Immagine troppo grande (massimo 8 MB)"); return; }
                  const r = new FileReader(); r.onload = () => setIcon(r.result); r.readAsDataURL(f);
                }} />
              </label>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Nome del server</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input"
            className="border-[#131620] bg-[#131620] text-white" />
        </div>

        <div className="mb-5">
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Descrizione</label>
          <Input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="settings-desc-input"
            className="border-[#131620] bg-[#131620] text-white" />
        </div>

        <Button onClick={save} disabled={saving} data-testid="settings-save-button"
          className="bg-[#5865F2] font-semibold text-white hover:bg-[#4752C4]">
          <Save className="mr-2 h-4 w-4" /> Salva impostazioni
        </Button>
      </Card>

      <Card className="max-w-2xl border-[#232838] bg-[#161923] p-6">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white"><KeyRound className="h-5 w-5 text-[#5865F2]" />Cambia password dashboard</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Password attuale</label>
            <Input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} data-testid="current-password-input" className="border-[#131620] bg-[#131620] text-white" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Nuova password</label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password-input" className="border-[#131620] bg-[#131620] text-white" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">Conferma nuova</label>
            <Input type="password" value={confPw} onChange={(e) => setConfPw(e.target.value)} data-testid="confirm-password-input" className="border-[#131620] bg-[#131620] text-white" />
          </div>
        </div>
        <Button onClick={changePassword} disabled={changingPw || !curPw || !newPw} data-testid="change-password-button"
          className="mt-4 bg-[#5865F2] font-semibold text-white hover:bg-[#4752C4]">
          {changingPw ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}Aggiorna password
        </Button>
        <p className="mt-2 text-xs text-[#949BA4]">La nuova password viene salvata in modo sicuro (hash) e sostituisce quella di default del file .env.</p>
      </Card>

      <Card className="max-w-2xl border-[#232838] bg-[#161923] p-6">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#5865F2]" />
          <div className="text-sm text-[#B5BAC1]">
            <p className="mb-1 font-semibold text-white">Connessione al bot</p>
            <p>La dashboard parla con il bot tramite il backend. Configura sul server le variabili
              <code className="mx-1 rounded bg-[#131620] px-1.5 py-0.5 text-[#DBDEE1]">BOT_API_URL</code>,
              <code className="mx-1 rounded bg-[#131620] px-1.5 py-0.5 text-[#DBDEE1]">BOT_API_KEY</code> e
              <code className="mx-1 rounded bg-[#131620] px-1.5 py-0.5 text-[#DBDEE1]">WEBHOOK_SECRET</code>.</p>
            <p className="mt-2">Stato attuale: <span className={demoMode ? "font-semibold text-[#FEE75C]" : "font-semibold text-[#23A55A]"}>
              {demoMode ? "Modalità demo (dati finti)" : "Collegato al bot reale"}</span></p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button onClick={testConnection} disabled={testing} data-testid="test-bot-connection-btn" className="bg-[#5865F2] font-semibold text-white hover:bg-[#4752C4]">
                {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />}Testa connessione bot
              </Button>
              {health && (
                <span data-testid="bot-connection-result" className={`text-sm font-medium ${health.error ? "text-[#F23F43]" : "text-[#23A55A]"}`}>
                  {health.error ? `Errore: ${health.error}` : `OK · pronto: ${health.ready ? "sì" : "no"}${health.ping ? " · " + health.ping + "ms" : ""}${health.mode ? " · " + health.mode : ""}`}
                </span>
              )}
            </div>
            <p className="mt-3 text-xs text-[#949BA4]">Per collegare il bot reale sulla tua VPS: imposta <code className="rounded bg-[#131620] px-1 py-0.5">BOT_API_URL</code> (es. <code className="rounded bg-[#131620] px-1 py-0.5">http://127.0.0.1:3001/api</code>), <code className="rounded bg-[#131620] px-1 py-0.5">BOT_API_KEY</code> e <code className="rounded bg-[#131620] px-1 py-0.5">WEBHOOK_SECRET</code> nel file <code className="rounded bg-[#131620] px-1 py-0.5">backend/.env</code>, poi riavvia il backend. Da quel momento ogni modifica qui viene applicata davvero al server Discord.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
